import React from "react"
import SEO from "./SEO"

const SITE_URL = "https://www.hotsnail.com.au"

const BlogPostSEO = ({
  title,
  slug,
  description,
  datePublished,
  dateModified,
  faqs,
  author = "HotSnail",
  image,
}) => {
  const CATEGORY_PREFIXES = ["guides/", "compare/", "tools/", "use-cases/", "alerts/"]
  const CATEGORY_LABELS = {
    "guides/": "Guides",
    "compare/": "Comparisons",
    "tools/": "Tools and references",
    "use-cases/": "Use cases",
    "alerts/": "Alerts",
  }

  // Blog posts pass a bare slug ("Australia-to-uk") and need the /blog/ prefix.
  // Category pages pass their own prefix ("guides/foo") and must not get it,
  // or og:url and the schema mainEntityOfPage id point at a URL that 404s.
  const category = CATEGORY_PREFIXES.find(p => slug.startsWith(p))
  // Trailing slash matches the canonical link and the sitemap; without it the
  // schema ids and og:url named a different URL from the one Google indexes.
  const pathname = category ? `/${slug}/` : `/blog/${slug}/`
  const url = `${SITE_URL}${pathname}`
  // Small imported images arrive as data: URIs; they cannot be a share image.
  const usableImage = image && !image.startsWith("data:") ? image : null
  const imageUrl = usableImage
    ? usableImage.startsWith("http")
      ? usableImage
      : `${SITE_URL}${usableImage}`
    : `${SITE_URL}/hotsnail/img/HotSnailLogo2.png`

  // The author is the business, not a person called "HotSnail". Pointing at the
  // homepage's Organization @id lets an engine join every page to one entity.
  const organization = { "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: "HotSnail" }

  const posting = {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: title,
    description: description,
    image: imageUrl,
    inLanguage: "en-AU",
    author: author === "HotSnail" ? organization : { "@type": "Person", name: author },
    publisher: {
      ...organization,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/hotsnail/img/HotSnailLogo2.png`,
      },
    },
    datePublished: datePublished,
    ...(dateModified && { dateModified }),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  }

  const schema = [posting]

  if (category) {
    const section = CATEGORY_LABELS[category]
    schema.push({
      "@type": "BreadcrumbList",
      itemListElement: [
        { name: "Home", item: `${SITE_URL}/` },
        { name: section, item: `${SITE_URL}/${category}` },
        { name: title, item: url },
      ].map((crumb, i) => ({ "@type": "ListItem", position: i + 1, ...crumb })),
    })
  }

  // faqs: [{ q, a }] for questions the page already shows as visible text.
  // `a` is plain text and must match what the reader sees.
  if (faqs && faqs.length) {
    schema.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: faqs.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    })
  }

  return (
    <SEO
      // No " - HotSnail Blog" suffix: it cost 17 characters of a ~60-char SERP
      // budget on all 93 pages, and mislabelled reference tables and comparisons
      // as blog posts. Google appends the site name itself where it helps.
      title={title}
      description={description}
      pathname={pathname}
      image={usableImage}
      article
      schema={schema}
    />
  )
}

export default BlogPostSEO
